/**
 * SignatureModal — adopt a signature/initial, styled like DocuSign's adopt dialog:
 * a bracketed "seal" with a caption, the name/initials in a handwritten font, and an ID line.
 *
 * The seal is rasterized to a PNG so the preview, the placed field, and the final PDF are the
 * exact same image (WYSIWYG). Drawn signatures are stored as-is.
 */
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { X, Loader2 } from 'lucide-react';
import { SIGNATURE_FONTS, fontStack, ensureSignatureFonts } from '../signatureFonts';

const SEAL_BLUE = '#2b579a';

function initialsFrom(name) {
  return (name || '').split(/\s+/).filter(Boolean).map((w) => w[0].toUpperCase()).join('').slice(0, 6);
}

// Capitalise the first letter of each word (e.g. "wally gaynor" -> "Wally Gaynor").
function toTitleCase(s) {
  return (s || '').replace(/(^|\s)([a-z])/g, (_, sp, ch) => sp + ch.toUpperCase());
}

function drawBracket(ctx, x, top, bottom, color, tick = 18) {
  const r = 5;       // corner radius
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x + tick, top);            // top tick (extended)
  ctx.lineTo(x + r, top);
  ctx.arcTo(x, top, x, top + r, r);     // rounded top-left corner
  ctx.lineTo(x, bottom - r);            // vertical spine
  ctx.arcTo(x, bottom, x + r, bottom, r); // rounded bottom-left corner
  ctx.lineTo(x + tick, bottom);         // bottom tick (extended)
  ctx.stroke();
}

/** Render a DocuSign-style seal (bracket + caption + name + id) to a PNG data URL. */
function rasterizeSeal(kind, { name, initials, stack, id }) {
  const scale = 3;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const isInit = kind === 'initials';
  const value = (isInit ? initials : name) || '';
  const caption = isInit ? '' : 'Signed by:';
  const idText = id || '';

  const capSize = 10;
  const idSize = 9;
  let nameSize = isInit ? 46 : 54;
  const maxNameW = isInit ? 150 : 380;

  // Measure the name's ACTUAL glyph extents — cursive fonts have tall ascenders / long
  // descenders that exceed the em box; we size the canvas to them so nothing is clipped.
  ctx.font = `${nameSize}px ${stack}`;
  let m = ctx.measureText(value);
  if ((m.width || 0) > maxNameW) {
    nameSize = Math.max(16, Math.floor(nameSize * maxNameW / m.width));
    ctx.font = `${nameSize}px ${stack}`;
    m = ctx.measureText(value);
  }
  const asc = m.actualBoundingBoxAscent || nameSize * 0.9;
  const desc = m.actualBoundingBoxDescent || nameSize * 0.4;
  const left = m.actualBoundingBoxLeft || 0;
  const right = m.actualBoundingBoxRight || m.width;
  const nameW = (left + right) || m.width;

  const bracketX = 4;
  const tick = 15;
  const contentX = bracketX + tick + 12;
  const padTop = 5, padBottom = 5, gap = 3;
  const capBlock = caption ? capSize + gap : 0;
  const idBlock = idText ? idSize + gap : 0;
  const h = Math.ceil(padTop + capBlock + (asc + desc) + idBlock + padBottom);

  ctx.font = `bold ${capSize}px Arial`;
  const capW = caption ? ctx.measureText(caption).width : 0;
  ctx.font = `${idSize}px "Courier New", monospace`;
  const idW = idText ? ctx.measureText(idText).width : 0;
  const contentW = Math.max(nameW, capW, idW, isInit ? 30 : 90);
  const w = Math.ceil(contentX + contentW + 14);

  canvas.width = w * scale;
  canvas.height = h * scale;
  ctx.scale(scale, scale);

  drawBracket(ctx, bracketX, 3, h - 3, SEAL_BLUE, tick);

  if (isInit) {
    ctx.fillStyle = SEAL_BLUE;
    ctx.font = 'bold 9px Arial';
    ctx.textBaseline = 'top';
    ctx.fillText('DS', w - 20, 4);
  } else {
    ctx.fillStyle = '#333333';
    ctx.font = `bold ${capSize}px Arial`;
    ctx.textBaseline = 'top';
    ctx.fillText(caption, contentX, padTop);
  }

  // Name — baseline placed so ascenders AND descenders fully fit inside the canvas.
  ctx.fillStyle = '#111111';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `${nameSize}px ${stack}`;
  ctx.fillText(value, contentX + left, padTop + capBlock + asc);

  if (idText) {
    ctx.fillStyle = '#8a8a8a';
    ctx.font = `${idSize}px "Courier New", monospace`;
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(idText, contentX, h - padBottom);
  }
  return canvas.toDataURL('image/png');
}

export default function SignatureModal({ open, onClose, onAdopt, defaultName = '', initialsOnly = false, sealId = '' }) {
  const [tab, setTab] = useState('style');
  const [name, setName] = useState('');
  const [initials, setInitials] = useState('');
  const [fontIdx, setFontIdx] = useState(0);
  const [fontsReady, setFontsReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);   // saving the adopted signature
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  useEffect(() => {
    ensureSignatureFonts();
    let alive = true;
    // Canvas can only draw a font that's already loaded, and @font-face fonts don't load until
    // used — so explicitly load every style (and its fallback) before rasterizing the previews.
    if (document.fonts && document.fonts.load) {
      const loads = SIGNATURE_FONTS.flatMap((def) => [
        document.fonts.load(`58px '${def.label}'`).catch(() => {}),
        document.fonts.load(`58px '${def.fallback}'`).catch(() => {}),
      ]);
      Promise.all(loads).then(() => alive && setFontsReady(true));
    } else {
      setFontsReady(true);
    }
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    setName(toTitleCase(defaultName));
    setInitials(initialsFrom(defaultName));
    setTab('style');
    setSubmitting(false);
  }, [defaultName, open]);

  const idText = useMemo(() => {
    const base = (sealId || 'COLLINGS-ESIGN').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    return `${base.slice(0, 15)}...`;
  }, [sealId]);

  // --- draw handlers ---
  const start = (e) => { drawing.current = true; hasDrawn.current = true; draw(e); };
  const end = () => { drawing.current = false; const c = canvasRef.current?.getContext('2d'); c && c.beginPath(); };
  const draw = (e) => {
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    // The canvas has a fixed 440x150 drawing buffer but is stretched to the
    // container width (w-full), so CSS pixels rarely equal buffer pixels. Scale
    // the pointer position by buffer/display ratio or the ink drifts away from
    // the finger (and shrinks into the top-left) on every non-440px width.
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * sx;
    const y = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * sy;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#111';
    ctx.lineTo(x, y); ctx.stroke(); ctx.beginPath(); ctx.moveTo(x, y);
  };
  const clearCanvas = () => {
    const c = canvasRef.current; c && c.getContext('2d').clearRect(0, 0, c.width, c.height); hasDrawn.current = false;
  };

  const adopt = async () => {
    if (submitting) return;
    // Validate before showing any spinner.
    if (tab === 'style' && !(initialsOnly ? initials.trim() : name.trim())) return;
    if (tab === 'draw' && !hasDrawn.current) return;

    setSubmitting(true);
    try {
      let payload;
      if (tab === 'style') {
        const def = SIGNATURE_FONTS[fontIdx];
        try {
          await Promise.all([
            document.fonts.load(`44px '${def.label}'`).catch(() => {}),
            document.fonts.load(`44px '${def.fallback}'`).catch(() => {}),
          ]);
          await document.fonts.ready;
        } catch { /* best-effort */ }
        payload = rasterizeSeal(initialsOnly ? 'initials' : 'signature',
          { name, initials, stack: fontStack(def), id: idText });
      } else {
        payload = canvasRef.current.toDataURL('image/png');
      }
      // Await in case the parent persists the signature to the server — this keeps
      // the spinner up until the save resolves, then closes.
      await onAdopt(payload);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  const previewName = name || 'Your name';
  const previewInit = initials || 'YN';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold">Adopt your {initialsOnly ? 'initials' : 'signature'}</h3>
          <button onClick={onClose} disabled={submitting} className="disabled:opacity-40"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <p className="text-xs text-gray-400 mb-4">Confirm your name, initials, and style.</p>

        <div className="mb-3">
          {initialsOnly ? (
            <>
              <label className="block text-sm font-medium mb-1">Initials</label>
              <input value={initials} onChange={(e) => setInitials(e.target.value.toUpperCase().slice(0, 6))}
                className="w-full border rounded px-3 py-2" />
            </>
          ) : (
            <>
              <label className="block text-sm font-medium mb-1">Full name</label>
              <input value={name} onChange={(e) => setName(toTitleCase(e.target.value))} className="w-full border rounded px-3 py-2" />
            </>
          )}
        </div>

        <div className="flex gap-2 mb-3">
          <button className={`px-3 py-1.5 rounded text-sm ${tab === 'style' ? 'bg-teal-600 text-white' : 'bg-gray-100'}`}
            onClick={() => setTab('style')}>Select style</button>
          <button className={`px-3 py-1.5 rounded text-sm ${tab === 'draw' ? 'bg-teal-600 text-white' : 'bg-gray-100'}`}
            onClick={() => setTab('draw')}>Draw</button>
        </div>

        {tab === 'style' ? (
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {SIGNATURE_FONTS.map((def, i) => {
              const stack = fontStack(def);
              // Only the preview matching this field's type: signature OR initials.
              const seal = initialsOnly
                ? rasterizeSeal('initials', { initials: previewInit, stack, id: idText })
                : rasterizeSeal('signature', { name: previewName, stack, id: idText });
              // key includes fontsReady so the <img> refreshes once the fonts finish loading.
              return (
                <button key={def.label} type="button" onClick={() => setFontIdx(i)}
                  className={`w-full flex items-center justify-start border rounded-lg p-3 ${fontIdx === i ? 'border-teal-500 bg-teal-50' : 'border-gray-200'}`}>
                  <img key={fontsReady ? 'r' : 'l'} src={seal} alt={def.label}
                    className="h-16 max-w-full object-contain object-left" />
                </button>
              );
            })}
          </div>
        ) : (
          <div>
            <canvas ref={canvasRef} width={440} height={150}
              className="border rounded w-full touch-none bg-white"
              onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={draw} onTouchEnd={end} />
            <button onClick={clearCanvas} className="text-xs text-gray-500 mt-1">Clear</button>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} disabled={submitting}
            className="px-4 py-2 text-sm rounded border disabled:opacity-50">Cancel</button>
          <button onClick={adopt} disabled={submitting}
            className="px-4 py-2 text-sm rounded bg-black text-white disabled:opacity-70 inline-flex items-center gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'Signing…' : 'Adopt & Sign'}
          </button>
        </div>
      </div>
    </div>
  );
}
